import { jsxs, jsx } from "react/jsx-runtime";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import "react";
import { useTranslation } from "react-i18next";
import { QUIZ_INTERACTIVE_STYLES } from "../shared/quiz-styles.js";
import { useQuizStyleVariant } from "../shared/quiz-style-context.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import { QuizImage } from "../shared/quiz-image.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useMultiChoiceQuiz } from "../hooks/use-multi-choice-quiz.js";
function MultiChoiceStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false
}) {
  const { t } = useTranslation("quiz");
  const variant = useQuizStyleVariant();
  const isAiq = variant === "aiq";
  const ix = QUIZ_INTERACTIVE_STYLES[variant];
  const {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit
  } = useMultiChoiceQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    quiz.subtype === "C" && /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: {
      __html: `
            @media (min-width: 768px) {
              #${gridContainerId} {
                grid-template-columns: repeat(${sortedAnswers.length}, minmax(0, 1fr));
              }
            }
          `
    } }),
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.multiChoice.label"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        dimTitleText: dimChoiceText,
        dimProblemText: dimChoiceText,
        dimMedia: dimChoiceText,
        problem: quiz.problem && /* @__PURE__ */ jsx("div", { className: cn(isAiq ? "text-[22px] font-medium leading-normal text-lc-text-primary" : "text-xl leading-relaxed text-gray-900"), children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) })
      }
    ),
    /* @__PURE__ */ jsx(
      "ul",
      {
        id: quiz.subtype === "C" ? gridContainerId : void 0,
        role: "listbox",
        "aria-label": t("types.multiChoice.selectOption"),
        "aria-multiselectable": "true",
        className: cn(
          "grid gap-4 mt-6",
          quiz.subtype === "B" && "grid-cols-1 md:grid-cols-2",
          quiz.subtype === "C" && "grid-cols-1",
          (!quiz.subtype || quiz.subtype !== "B" && quiz.subtype !== "C") && "grid-cols-1"
        ),
        children: sortedAnswers.map((answer, idx) => {
          const isSelected = selectedKeys.includes(answer.key);
          const isInResolvedCorrectAnswers = resolvedCorrectKeySet.has(answer.key);
          const isCorrectByCorrects = correctsBySelectedKey.get(answer.key);
          const showAsCorrect = isSolved && (shouldShowCorrectAnswers ? isInResolvedCorrectAnswers : isSelected && (isCorrectByCorrects !== void 0 ? isCorrectByCorrects : isInResolvedCorrectAnswers));
          const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect;
          const isDisplayedSelected = shouldShowCorrectAnswers ? isInResolvedCorrectAnswers : isSelected;
          const isCompleted = isSolved || isViewSolved;
          const hasImage = !!answer.image;
          return /* @__PURE__ */ jsxs(
            "li",
            {
              role: "option",
              "aria-selected": isDisplayedSelected,
              onClick: () => !isCompleted && handleSelect(answer.key),
              className: cn(
                "group w-full p-3 rounded-2xl border-2 text-[20px] font-medium text-left transition-all",
                "flex items-center cursor-pointer",
                // Text-only choices keep a comfortable tap target while growing for wrapped answers.
                !hasImage && "min-h-16",
                // 기본 상태
                !isSolved && !isSelected && ix.optionIdle,
                !isSolved && !isSelected && ix.optionIdleHover,
                // 선택 상태 (풀이 전)
                !isSolved && isSelected && ix.optionSelected,
                // 정답
                showAsCorrect && "text-lc-mint-900 bg-lc-mint-100 border-lc-mint-900 [box-shadow:unset]",
                // 오답
                isWrongSelected && "text-lc-red-900 bg-lc-red-100 border-lc-red-900 [box-shadow:unset]",
                // 비활성화 (풀이 후 미선택/미정답)
                isSolved && !showAsCorrect && !isWrongSelected && "border-lc-gray-400 text-gray-900 opacity-60",
                // 완료 상태
                isCompleted && "pointer-events-none"
              ),
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "w-8 h-8 mr-4 font-bold rounded-full shrink-0 text-[16px] flex items-center justify-center transition-all",
                      // 기본 상태
                      !isSelected && !isSolved && ix.badgeIdle,
                      !isSelected && !isSolved && ix.badgeIdleHover,
                      // 선택 상태 (풀이 전)
                      isSelected && !isSolved && ix.badgeSelected,
                      // 정답
                      showAsCorrect && "text-white bg-lc-mint-900",
                      // 오답
                      isWrongSelected && "text-white bg-lc-red-900",
                      // 비활성화
                      isSolved && !showAsCorrect && !isWrongSelected && "text-lc-gray-500 bg-lc-gray-100"
                    ),
                    children: idx + 1
                  }
                ),
                answer.image && /* @__PURE__ */ jsx("div", { className: cn(dimChoiceText && "opacity-[0.05]"), children: /* @__PURE__ */ jsx(
                  QuizImage,
                  {
                    image: answer.image,
                    alt: t("types.multiChoice.optionAlt", { number: idx + 1 }),
                    className: "my-0 shrink-0"
                  }
                ) }),
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    "data-quiz-choice-text": true,
                    className: cn("flex-1 text-[20px] font-medium", dimChoiceText && "opacity-[0.05]"),
                    children: /* @__PURE__ */ jsx(KatexText, { text: answer.answer })
                  }
                )
              ]
            },
            answer.key
          );
        })
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
export {
  MultiChoiceStudent
};
//# sourceMappingURL=multi-choice-student.js.map
